// The one authorization primitive, shared by server route guards and client UI guards.
// Effective permissions = super-admin bypass, else the role's hardcoded defaults with
// DB-backed grant/revoke overrides applied on top.
import { DEFAULT_ROLE_PERMISSIONS, PERMISSIONS, type PermissionKey, type Role } from './permissions.js';

export interface PermissionOverride {
  permissionKey: string;
  effect: 'grant' | 'revoke';
}

const ALL_KEYS: string[] = PERMISSIONS.map((p) => p.key);

/** Resolve the effective permission-key set for a role given its DB overrides. */
export function computeEffectivePermissions(role: Role, overrides: PermissionOverride[] = []): string[] {
  if (role === 'super_admin') return [...ALL_KEYS];
  const set = new Set<string>(DEFAULT_ROLE_PERMISSIONS[role]);
  for (const o of overrides) {
    if (o.effect === 'grant') set.add(o.permissionKey);
    else set.delete(o.permissionKey);
  }
  return [...set];
}

/** The gate used everywhere. Accepts anything carrying a role + effective permission list. */
export function can(
  user: { role: Role; permissions: string[] } | null | undefined,
  permission: PermissionKey | string,
): boolean {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  return user.permissions.includes(permission);
}
